/** biome-ignore-all lint/style/useNamingConvention: PascalCase is needed for `ComparableChanges` */

import { Divider, Stack, styled, Typography } from "@mui/material";
import type { ReactNode } from "react";

import { formatNumberWithSeparators } from "~/utils/unitConversion";
import { Chip, type ChipProps } from "../..";
import { METRIC_COLOR } from "./MetricCard";
import type { MetricGroup, MetricName } from "./metricCard.types";

interface CardIconProps {
  cardColor: ChipProps["color"];
}

export interface ComparableChanges {
  YoY: number | null;
  QoQ: number | null;
  MoM: number | null;
  PTO: number | null;
}

interface MetricCardProps {
  metricGroup: MetricGroup;
  metricName: MetricName;
  comparableChanges: ComparableChanges;
  value: number | null;
  period: string;
  description?: string;
  units?: string | null;
  icon: ReactNode;
}

const Container = styled(Stack)(({ theme }) => ({
  width: "400px",
  borderRadius: "12px",
  border: `1px solid ${theme.palette.divider}`,
  overflow: "hidden",
}));

const CardIcon = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "cardColor",
})<CardIconProps>(({ theme, cardColor = "primary" }) => ({
  padding: theme.spacing(0, 2),
  justifyContent: "center",
  background: theme.palette[cardColor].gradient.background,
}));

const CardContent = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  gap: "12px",
  flex: "1",
  borderLeft: `1px solid ${theme.palette.divider}`,
}));

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { MetricCardDetail } from '@monthly/ui' */
export function MetricCardDetail({
  metricGroup,
  metricName,
  comparableChanges,
  value,
  description,
  icon,
  units,
  period,
}: MetricCardProps) {
  const metricColor = METRIC_COLOR[metricGroup]?.color || "primary";
  return (
    <Container direction={"row"}>
      <CardIcon cardColor={metricColor as ChipProps["color"]}>
        <Stack width={"88px"} height={"88px"} alignItems={"center"}>
          {icon}
        </Stack>
      </CardIcon>
      <CardContent>
        <Stack gap={0.5}>
          <Typography
            variant="h5"
            sx={(theme) => ({
              color: theme.palette.secondary.dark,
            })}
          >
            {metricName}
          </Typography>
          <Typography variant="overline">{description}</Typography>
        </Stack>
        <Divider
          sx={(theme) => ({
            borderColor: theme.palette.divider,
          })}
        />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.secondary.dark,
              fontWeight: theme.typography.fontWeightSemiBold,
            })}
          >
            {period}
          </Typography>
          <Stack direction="row" alignItems="baseline">
            <Typography
              variant="h4"
              sx={(theme) => ({
                color: theme.palette.secondary.dark,
              })}
            >
              {value === null ? "-" : formatNumberWithSeparators(value)}
            </Typography>
            <Typography
              variant="body1"
              sx={(theme) => ({
                color: theme.palette.secondary.main,
              })}
            >
              {units}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction={"row"} gap={1} alignItems="center">
          {Object.entries(comparableChanges).map(([key, change]) => {
            if (change === null || change === undefined) {
              return null;
            }

            return (
              <Chip
                key={key}
                label={`${key} ${change}${units === "%" ? "p" : "%"}`}
                size="small"
                color="secondary"
              />
            );
          })}
        </Stack>
      </CardContent>
    </Container>
  );
}
